<template>
  <layout page-title="Markentypologie">
    <!-- Primary column -->
    <section
      class="
        min-w-0
        flex-1
        h-full
        flex flex-col
        lg:order-last
        relative
        pt-2
        md:pt-6
        lg:pt-0
      "
    >
      <div class="mt-4 px-4 md:px-6 w-full lg:px-8 2xl:max-w-5xl">
        <div class="flex flex-col mb-6">
          <div class="-my-2 py-2 md:-mx-6 md:px-6 lg:-mx-8 lg:px-8">
            <!-- Page Header: start -->
            <div class="md:flex md:items-center md:justify-between">
              <div class="flex-1 min-w-0">
                <h2
                  class="
                    text-2xl
                    font-light
                    leading-7
                    text-gray-900
                    md:text-2xl md:leading-9 md:truncate
                  "
                >
                  Markentypologie
                </h2>
              </div>
              <div class="mt-4 flex md:mt-0 md:ml-4"></div>
            </div>
            <!-- Page Header: end -->

            <div class="flex flex-col md:flex-row justify-between mt-4">
              <div class="md:mr-8 md:border-r border-gray-300 md:pr-8 md:w-1/2">
                <div class="mb-3">
                  <!-- bg-matrix-pattern -->
                  <div
                    class="border-b border-gray-200 bg-gray-400 rounded-full mx-auto w-3/4 sm:w-1/2 md:w-full"
                  >
                    <svg-matrix
                      :active-zones="this.selectedArchetype.activeZones"
                      :validity="validity"
                      :active-area="this.selectedArchetype.zone"
                      :selectedWords="selectedWords"
                    ></svg-matrix>
                  </div>
                </div>
              </div>

              <div class="md:mr-8 md:pr-8 md:w-1/2">
                <div class="mb-3">
                  <div class="border-b border-gray-200">
                    <autocomplete
                      v-on:keyup.191="focusAutocomplete"
                      ref="autocomplete_ref"
                      @selected="wordSelected"
                      :isAsync="true"
                    ></autocomplete>
                  </div>

                  <ul
                    class="
                      mt-3
                      grid grid-cols-1
                      gap-3
                      md:gap-2 md:grid-cols-2
                      lg:grid-cols-1
                      xl:grid-cols-2
                    "
                  >
                    <selected-word
                      v-for="(word, idx) in selectedWords"
                      :key="idx"
                      :word="word"
                      @removed="wordRemoved"
                    ></selected-word>
                  </ul>

                  <div
                    v-if="internalError"
                    class="rounded-md bg-red-50 p-4 mt-4"
                  >
                    <div class="flex">
                      <div class="flex-shrink-0">
                        <!-- Heroicon name: solid/x-circle -->
                        <svg
                          class="h-5 w-5 text-red-400"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </div>
                      <div class="ml-3">
                        <h3 class="text-sm font-medium text-red-800">
                          Interner Hinweis: Es ist ein Problem aufgetreten.
                        </h3>
                      </div>
                    </div>
                  </div>

                  <div v-if="loading" class="mt-3 w-full h-12 relative">
                    <spinner :busy="loading" color="#53C9FF"></spinner>
                  </div>

                  <div
                    class="
                      mt-3
                      grid grid-cols-3
                      gap-3
                      md:gap-2 
                      md:grid-cols-4
                      lg:grid-cols-3
                      xl:grid-cols-4
                      3xl:grid-cols-4
                    "
                  >
                    <archetype
                      v-for="(archetype, idx) in archetypes"
                      :archetype="archetype"
                      :key="idx"
                      @selected="selectArchetype(archetype)"
                      :selected="selectedArchetype"
                    ></archetype>
                  </div>
                </div>
              </div>

              <!-- Dashboard: end -->
            </div>
          </div>
        </div>
      </div>


    </section>

  </layout>
</template>

<script>
import Layout from '@/Pages/Markencode/Shared/Layout';

import Spinner from '@/Shared/Spinner.vue';
import SvgMatrix from '@/Pages/Markencode/BrandTypology/Shared/SvgMatrix.vue';
import Autocomplete from '@/Pages/Markencode/BrandTypology/Shared/Autocomplete.vue';
import Archetype from '@/Pages/Markencode/BrandTypology/Shared/Archetype.vue';
import SelectedWord from '@/Pages/Markencode/BrandTypology/Shared/SelectedWord.vue';

import { map } from 'lodash';

export default {
  components: {
    Layout,
    Spinner,
    SvgMatrix,
    Autocomplete,
    Archetype,
    SelectedWord,
  },

  data() {
    return {
      loading: false,
      selectedArchetype: {},
      validity: null,
      internalError: false,
      selectedWords: [],
      archetypes: [],
    };
  },

  watch: {
    selectedWords: {
      handler: function () {
        this.archetypes = [];
        this.selectedArchetype = {};
        this.validity = null;
        this.internalError = false;

        if (this.selectedWords.length < 3) {
          return false;
        }

        this.loading = true;

        window.axios
        .post(
          this.route('markencode.brand-typology.calculate', {
            
          }),
          {
            selectedWords: map(this.selectedWords, 'id'),
          }
        )
        .then((response) => {
          this.archetypes = response.data.archetypes;
            this.validity = response.data.allWordsFitInValueZone;
            this.loading = false;
        })
        .catch((error) => {
            this.loading = false;
            this.internalError = true;
          });

        // window.axios
        //   .post(
        //     this.route('markencode.brand-typology.calculate'), 
        //     {
        //       selectedWords: map(this.selectedWords, 'id'),
        //     }
        //   )
        //   .then((response) => {
        //     // console.log(response);
        //     this.archetypes = response.data.archetypes;
        //     this.validity = response.data.allWordsFitInValueZone;
        //     this.loading = false;
        //   })
        //   .catch((error) => {
        //     this.loading = false;
        //     this.internalError = true;
        //   });
      },
    },
  },

  mounted() {
    this.focusAutocomplete();
  },

  methods: {
    focusAutocomplete() {
      this.$nextTick(() => {
        this.$refs.autocomplete_ref.$el.querySelector('input').focus();
      });
    },
    
    wordSelected(word) {
      this.selectedWords.push(word);
      this.focusAutocomplete();
    },
    wordRemoved(word) {
      this.selectedWords = this.selectedWords.filter(function (selectedWord) {
        return selectedWord.id !== word.id;
      });
      this.focusAutocomplete();
    },
    selectArchetype(archetype) {
      this.validity = null;
      this.selectedArchetype = archetype;
    },
  },

  computed: {
    
  },
};
</script>